import React from 'react'
import clsx from 'clsx'
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Radio, RadioGroup } from '@wartek-id/radio'
import { CustomSelect } from '@wartek-id/select'
import { Text } from '@wartek-id/text-v1'

import {
  MIN_ACTIVITY_POINTS,
  MAX_ACTIVITY_POINTS,
} from 'app/KinerjaPSPegawaiPage/constants/pengembanganKompetensi'
import { usePengembanganKompetensiDraftStore } from 'app/KinerjaPSPegawaiPage/stores/kinerja-ps'
import { usePengembanganKompetensi } from 'app/KinerjaPSPegawaiPage/hooks/usePengembanganKompetensi'
import usePrevious from 'utils/hooks/usePrevious'
import { usePerencanaanPS } from 'app/KinerjaPSPegawaiPage/hooks/usePerencanaan'

import type {
  TPengembanganKompetensiKategoriOption,
  TPengembanganKompetensiKegiatanOption,
} from 'types/kinerja-ps-pegawai'

type TActivityCategoryDropdownProps = {
  index: number
  isDisabled: boolean
}
const ActivityCategoryDropdown: React.FC<TActivityCategoryDropdownProps> = ({
  index,
  isDisabled,
}) => {
  const { draftActivities } = usePengembanganKompetensiDraftStore()
  const [showAllActivities, setShowAllActivities] = React.useState(false)

  const { kompetensiKategoriData } = usePengembanganKompetensi()

  const { control, watch } = useFormContext()
  const indicatorValue = watch('indicator')
  const hasIndicatorValue = Boolean(indicatorValue)
  const activitiesValue = useWatch({ name: 'activities', control })

  const initiallyShowCategoryOptions = React.useMemo(() => {
    if (hasIndicatorValue) {
      return [
        ...kompetensiKategoriData
          .filter(({ isInitiallyShown }) => Boolean(isInitiallyShown))
          .map(({ nama: name, ...rest }) => ({
            ...rest,
            name,
          })),
        // append "Show more" button
        {
          id: 'showMore',
          name: 'Tampilkan pilihan lainnya',
          isInitiallyShown: true,

          // need to be disabled to prevent options popup from being closed
          disabled: true,
        },
      ]
    }

    return []
  }, [hasIndicatorValue, kompetensiKategoriData])

  const options = React.useMemo(() => {
    const diplayedOptions = showAllActivities
      ? kompetensiKategoriData.map(({ nama: name, ...rest }) => ({
          ...rest,
          name,
        }))
      : initiallyShowCategoryOptions
    return diplayedOptions
  }, [initiallyShowCategoryOptions, kompetensiKategoriData, showAllActivities])

  const selectedKategoriValue = options.find(
    ({ id }) => id === activitiesValue?.[index]?.kategori
  )

  React.useEffect(() => {
    if (
      Boolean(activitiesValue?.[index]?.kategori) &&
      !Boolean(
        initiallyShowCategoryOptions.find(
          ({ id }) => id === activitiesValue?.[index]?.kategori
        )
      )
    ) {
      setShowAllActivities(true)
      return
    }

    setShowAllActivities(false)

    return () => {
      setShowAllActivities(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftActivities, index])

  return (
    <Controller
      control={control}
      name={`activities[${index}].kategori`}
      rules={{
        required: 'Wajib diisi.',
      }}
      render={({ field: { onChange }, fieldState: { error } }) => {
        return (
          <>
            <div className="flex justify-between">
              <label htmlFor={`activities[${index}].kategori`}>
                <Text variant="heading-sm" className="mb-2">
                  Pilih yang Anda butuhkan untuk meningkatkan indikator
                </Text>
              </label>

              {Boolean(error?.message) && (
                <Text color="critical">{error.message}</Text>
              )}
            </div>

            <CustomSelect
              data-testid="kategori-dropdown"
              placeholder="Pilih di sini"
              className={clsx(
                Boolean(error?.message) && 'border-critical-default'
              )}
              value={selectedKategoriValue}
              options={options}
              customList={({ item }) => {
                if (
                  !showAllActivities &&
                  (item as TPengembanganKompetensiKategoriOption).disabled
                ) {
                  return (
                    <div
                      className="cursor-pointer text-blue-60"
                      onClick={() => {
                        setShowAllActivities(true)
                      }}
                    >
                      {(item as TPengembanganKompetensiKategoriOption).name}
                    </div>
                  )
                }

                return (
                  <div className="flex items-center -mt-1">
                    {(item as TPengembanganKompetensiKategoriOption).name}
                  </div>
                )
              }}
              customDisplayValue={({ item }) => {
                return (
                  <div className="flex items-center -mt-1">
                    {(item as TPengembanganKompetensiKategoriOption).name}
                  </div>
                )
              }}
              isDisabled={isDisabled || !hasIndicatorValue}
              isInvalid={Boolean(error?.message)}
              onChange={(item) => {
                onChange((item as TPengembanganKompetensiKategoriOption).id)
              }}
            />
          </>
        )
      }}
    />
  )
}

type TActivityDropdownProps = {
  index: number
  isDisabled: boolean
}
const ActivityDropdown: React.FC<TActivityDropdownProps> = ({
  index,
  isDisabled,
}) => {
  const { control, setValue, watch } = useFormContext()
  const indicatorValue = watch('indicator')
  const hasIndicatorValue = Boolean(indicatorValue)

  const activitiesValue = useWatch({ name: 'activities', control })
  const kategoriId = activitiesValue?.[index]?.kategori ?? null

  const { kompetensiKegiatanData } = usePengembanganKompetensi(
    kategoriId ?? null
  )

  const prevSelectedKategoriId = usePrevious(kategoriId)

  const options = React.useMemo(() => {
    return kompetensiKegiatanData.map((item) => {
      const { jenisKegiatan: name, ...rest } = item

      const hasSelectedBefore = activitiesValue.some(
        ({ kategori, kegiatan }) =>
          kategori === kategoriId && kegiatan === item.id
      )

      if (hasSelectedBefore) {
        return {
          ...rest,
          name,
          disabled: true,
        }
      }

      return {
        ...rest,
        name,
      }
    })
  }, [activitiesValue, kategoriId, kompetensiKegiatanData])

  const selectedKegiatanValue = options.find(
    ({ id }) => id === activitiesValue?.[index]?.kegiatan
  )

  React.useEffect(() => {
    if (
      Boolean(kategoriId) &&
      Boolean(prevSelectedKategoriId) &&
      prevSelectedKategoriId !== kategoriId
    ) {
      setValue(`activities[${index}].kegiatan`, undefined)
      setValue(`activities[${index}].poin`, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kategoriId])

  React.useEffect(() => {
    setValue(`activities[${index}].poin`, selectedKegiatanValue?.poin ?? 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKegiatanValue?.poin])

  return (
    <Controller
      control={control}
      name={`activities[${index}].kegiatan`}
      rules={{
        required: 'Wajib diisi.',
      }}
      render={({ field: { onChange }, fieldState: { error } }) => {
        return (
          <>
            <div className="flex justify-between">
              <label htmlFor={`activities[${index}].kegiatan`}>
                <Text variant="heading-sm" className="mb-2">
                  Pilih cara untuk meningkatkan Indikator
                </Text>
              </label>

              {Boolean(error?.message) && (
                <Text color="critical">{error.message}</Text>
              )}
            </div>
            <CustomSelect
              data-testid="kegiatan-dropdown"
              placeholder="Pilih di sini"
              className={clsx(
                Boolean(error?.message) && 'border-critical-default'
              )}
              options={options}
              customList={({ item }) => {
                if ((item as TPengembanganKompetensiKegiatanOption).disabled) {
                  return (
                    <div className="cursor-not-allowed">
                      {(item as TPengembanganKompetensiKegiatanOption).name}
                    </div>
                  )
                }

                return (
                  <div className="flex items-center -mt-1">
                    {(item as TPengembanganKompetensiKegiatanOption).name}
                  </div>
                )
              }}
              value={selectedKegiatanValue}
              customDisplayValue={({ item }) => {
                return (
                  <div className="flex items-center -mt-1">
                    {(item as TPengembanganKompetensiKegiatanOption).name}
                  </div>
                )
              }}
              isDisabled={
                isDisabled ||
                !hasIndicatorValue ||
                !Boolean(activitiesValue?.[index]?.kategori)
              }
              isInvalid={Boolean(error?.message)}
              onChange={(item) => {
                onChange((item as TPengembanganKompetensiKegiatanOption).id)
              }}
            />
          </>
        )
      }}
    />
  )
}

export const ActivitiesForm = () => {
  const { isPengembanganKompetensiEditable } = usePerencanaanPS()

  const {
    control,
    formState: { errors, isValid },
    trigger,
    watch,
  } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'activities',
  })

  const indicatorValue = watch('indicator')
  const activitiesValue = useWatch({ name: 'activities', control })
  const watchedFields = fields.map((field, index) => {
    return {
      ...field,
      ...activitiesValue[index],
    }
  })
  const hasIndicatorValue = Boolean(indicatorValue)
  const showAddActivityButton = React.useMemo(() => {
    return (
      (isPengembanganKompetensiEditable &&
        activitiesValue.length === 1 &&
        isValid) ||
      activitiesValue.length > 1
    )
  }, [activitiesValue.length, isPengembanganKompetensiEditable, isValid])
  const isAddActivityButtonDisabled = React.useMemo(() => {
    const lastField = watchedFields[watchedFields.length - 1] ?? {}
    return (
      watchedFields.length > 1 &&
      Object.keys(lastField).filter(
        (key) => key !== 'id' && Boolean(lastField?.[key])
      ).length === 0
    )
  }, [watchedFields])
  const isActivitiesPointFulfilled = React.useMemo(() => {
    const sum = watchedFields.reduce((acc, field) => {
      return acc + (field?.poin ?? 0)
    }, 0)

    if (sum >= MIN_ACTIVITY_POINTS && sum <= MAX_ACTIVITY_POINTS) {
      return true
    }

    return false
  }, [watchedFields])

  const handleAddActivity = async () => {
    const isValidAfterTrigger = await trigger()

    if (isValidAfterTrigger) {
      append({
        activity: undefined,
        activityDetail: undefined,
        lokasi: undefined,
      })
    }
  }

  return (
    <div className="space-y-6">
      {fields.map((field, index) => (
        <div key={field.id} className="space-y-4">
          <div className="flex justify-between">
            <Text variant="heading-md">Kegiatan {index + 1}</Text>
            {isPengembanganKompetensiEditable && index > 0 && (
              <Button color="red" size="sm" onClick={() => remove(index)}>
                Hapus
              </Button>
            )}
          </div>

          <div
            className={clsx(
              'p-6 space-y-6 border rounded-md border-subdued',
              Object.values(errors?.activities?.[index] ?? {}).some(
                ({ message }) => Boolean(message)
              ) && 'border-critical-default'
            )}
          >
            {/** Activity */}
            <div className="mb-6">
              <ActivityCategoryDropdown
                index={index}
                isDisabled={!isPengembanganKompetensiEditable}
              />
            </div>

            {/** Activity Detail */}
            <div className="mb-6">
              <ActivityDropdown
                index={index}
                isDisabled={!isPengembanganKompetensiEditable}
              />
            </div>

            {/** Location */}
            <div className="mb-6">
              <Controller
                control={control}
                name={`activities[${index}].lokasi`}
                rules={{
                  required: 'Wajib diisi.',
                }}
                render={({
                  field: { name, onChange, value },
                  fieldState: { error },
                }) => {
                  return (
                    <>
                      <div className="flex justify-between">
                        <label htmlFor={`activities[${index}].lokasi`}>
                          <Text variant="heading-sm" className="mb-2">
                            Pilih di mana Anda akan meningkatkan Indikator
                          </Text>
                        </label>

                        {Boolean(error?.message) && (
                          <Text color="critical">{error.message}</Text>
                        )}
                      </div>

                      <RadioGroup
                        name={name}
                        onChange={onChange}
                        className="flex flex-col"
                        value={value}
                      >
                        <Radio
                          data-testid="location-inside-radio"
                          checked={
                            value === 'DI_DALAM_LINGKUNGAN_DINAS_PENDIDIKAN'
                          }
                          value="DI_DALAM_LINGKUNGAN_DINAS_PENDIDIKAN"
                          disabled={
                            !isPengembanganKompetensiEditable ||
                            !hasIndicatorValue ||
                            !Boolean(activitiesValue?.[index]?.kegiatan)
                          }
                        >
                          Dilakukan secara mandiri
                        </Radio>
                        <Radio
                          data-testid="location-outside-radio"
                          checked={
                            value === 'DI_LUAR_LINGKUNGAN_DINAS_PENDIDIKAN'
                          }
                          value="DI_LUAR_LINGKUNGAN_DINAS_PENDIDIKAN"
                          className="mt-2"
                          disabled={
                            !isPengembanganKompetensiEditable ||
                            !hasIndicatorValue ||
                            !Boolean(activitiesValue?.[index]?.kegiatan)
                          }
                        >
                          Dilakukan di dalam komunitas atau organisasi profesi
                          di dalam lingkungan Dinas Pendidikan
                        </Radio>
                      </RadioGroup>
                    </>
                  )
                }}
              />
            </div>
          </div>
        </div>
      ))}

      {showAddActivityButton && (
        <div className="flex items-center justify-end space-x-4">
          {isActivitiesPointFulfilled ? (
            <div className="flex items-center space-x-2">
              <Icon color="success">check</Icon>
              <Text color="success" variant="action">
                Cara yang dipilih sudah cukup untuk meningkatkan Indikator
              </Text>
            </div>
          ) : (
            <Text color="subdued" variant="helper">
              Anda dapat menambahkan kegiatan lain untuk meningkatkan Indikator
            </Text>
          )}

          {isPengembanganKompetensiEditable && (
            <Button
              className="!px-10"
              color={isActivitiesPointFulfilled ? 'white' : 'black'}
              disabled={isAddActivityButtonDisabled}
              onClick={handleAddActivity}
            >
              Tambah
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
